import { useState, useRef } from 'react'
import styles from './HomePage.module.css'
import { Button, ScrollReveal, StaggerContainer, AnimatedCounter } from '../../components'
import { HeroSection } from '../../components/Hero'
import { Lightbox } from '../../components/Lightbox'
import { PlasticJourney } from '../../components/PlasticJourney'

/* ─── Gallery images — brandbook lifestyle shots ─── */
const galleryImages = [
    { src: '/brandbook/21_caratulas.svg', alt: 'Armazón negro con carátula amarilla puesta y un estuche tipo red' },
    { src: '/brandbook/39_modelo.svg', alt: 'Chico con lentes Tortu-k y carátula azul en la naturaleza' },
    { src: '/brandbook/40_ejemplo_red.svg', alt: 'Armazón transparente y carátula roja en la naturaleza' },
    { src: '/brandbook/25_modelo_opt.svg', alt: 'Mujer en la playa usando lentes Tortu-k — carátula azul' },
    { src: '/brandbook/8_set.svg', alt: 'Colección de carátulas Tortu-k sobre superficie de concreto con tarjetas de marca' },
    { src: '/brandbook/26_modelo_sol.svg', alt: 'Close-up de mujer en playa con lentes Tortu-k azul' },
]

/* ─── Cover catalog ─── */
const covers = [
    { image: '/brandbook/14_red.svg', color: 'var(--color-cover-red)', label: 'Rojo', id: 'red' },
    { image: '/brandbook/12_yellow.svg', color: 'var(--color-cover-yellow)', label: 'Amarillo', id: 'yellow' },
    { image: '/brandbook/13_green.svg', color: 'var(--color-cover-green)', label: 'Verde', id: 'green' },
    { image: '/brandbook/11_blue.svg', color: 'var(--color-cover-blue)', label: 'Azul', id: 'blue' },
]

export function HomePage() {
    const [lightboxOpen, setLightboxOpen] = useState(false)
    const [lightboxIndex, setLightboxIndex] = useState(0)

    const openLightbox = (i: number) => { setLightboxIndex(i); setLightboxOpen(true) }

    const carouselRef = useRef<HTMLDivElement>(null)
    const scrollCarousel = (dir: 'prev' | 'next') => {
        const track = carouselRef.current
        if (!track) return
        const slide = track.querySelector('[data-slide]') as HTMLElement | null
        const slideWidth = slide ? slide.offsetWidth + 8 : 520
        track.scrollBy({ left: dir === 'next' ? slideWidth : -slideWidth, behavior: 'smooth' })
    }

    const [isDragging, setIsDragging] = useState(false)
    const dragMoved = useRef(false)
    const startX = useRef(0)
    const scrollLeft = useRef(0)

    const handleMouseDown = (e: React.MouseEvent) => {
        const track = carouselRef.current
        if (!track) return
        setIsDragging(true)
        dragMoved.current = false
        startX.current = e.pageX - track.offsetLeft
        scrollLeft.current = track.scrollLeft
    }

    const handleMouseLeave = () => {
        setIsDragging(false)
    }

    const handleMouseUp = () => {
        setIsDragging(false)
    }

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging) return
        e.preventDefault()
        const track = carouselRef.current
        if (!track) return
        const x = e.pageX - track.offsetLeft
        const walk = (x - startX.current) * 1.5
        if (Math.abs(walk) > 5) {
            dragMoved.current = true
        }
        track.scrollLeft = scrollLeft.current - walk
    }

    const handleSlideClick = (i: number, e: React.MouseEvent) => {
        if (dragMoved.current) {
            e.preventDefault()
            e.stopPropagation()
            return
        }
        openLightbox(i)
    }


    return (
        <div className={styles.container}>

            {/* ── Hero ── */}
            <HeroSection />

            {/* ── Impact bar ── */}
            <div className={styles.impactBar}>
                <div className={styles.impactBarInner}>
                    <div className={styles.impactStat}>
                        <AnimatedCounter value={75} suffix="%" className={styles.impactValue} />
                        <span className={styles.impactLabel}>Plástico reciclado</span>
                    </div>
                    <div className={styles.impactDivider} />
                    <div className={styles.impactStat}>
                        <span className={styles.impactValue}>4</span>
                        <span className={styles.impactLabel}>Carátulas intercambiables</span>
                    </div>
                    <div className={styles.impactDivider} />
                    <div className={styles.impactStat}>
                        <span className={styles.impactValue}>∞</span>
                        <span className={styles.impactLabel}>Combinaciones posibles</span>
                    </div>
                    <div className={styles.impactDivider} />
                    <div className={styles.impactStat}>
                        <span className={styles.impactValue}>1</span>
                        <span className={styles.impactLabel}>Marco para toda la vida</span>
                    </div>
                </div>
            </div>

            {/* ── El Sistema — How it works ── */}
            <section className={styles.sistemaSection}>
                <div className={styles.sistemaSectionInner}>
                    <ScrollReveal>
                        <p className={styles.eyebrow}>El sistema</p>
                        <h2 className={styles.sectionTitle}>
                            ¿Cómo funciona?<br />Carátulas magnéticas intercambiables.
                        </h2>
                        <p className={styles.sectionSubtitle}>
                            Un marco base de por vida de alta calidad. Carátulas magnéticas intercambiables de plástico reciclado.
                            ¡Cambia tu estilo y escucha el clic!
                        </p>
                    </ScrollReveal>

                    <div className={styles.sistemaGrid}>
                        <ScrollReveal direction="left">
                            <div className={styles.sistemaImageWrap}>
                                <img
                                    src="/brandbook/15_instruc.svg"
                                    alt="Instructivo ECO-SHIFT: 1. Alinea, 2. Conecta — tu nuevo estilo listo para usar"
                                    className={styles.sistemaImage}
                                />
                            </div>
                        </ScrollReveal>

                        <ScrollReveal direction="right">
                            <div className={styles.sistemaText}>
                                <div className={styles.sistemaFeatures}>
                                    {[
                                        {
                                            color: 'var(--color-cover-yellow)',
                                            title: 'Alinea',
                                            desc: 'El contorno de la carátula encaja perfectamente sobre el armazón base. Sin herramientas, sin esfuerzo.',
                                        },
                                        {
                                            color: 'var(--color-cover-green)',
                                            title: 'Conecta',
                                            desc: 'El sistema magnético CLIC hace la conexión en fracciones de segundo. La carátula queda firme.',
                                        },
                                        {
                                            color: 'var(--color-cover-blue)',
                                            title: 'Listo',
                                            desc: 'Nuevo look, misma esencia. Repite cuando quieras. Tu colección crece, tu marco no cambia.',
                                        },
                                    ].map((f) => (
                                        <div className={styles.sistemaFeature} key={f.title}>
                                            <div
                                                className={styles.sistemaFeatureDot}
                                                style={{ background: f.color }}
                                            />
                                            <div className={styles.sistemaFeatureText}>
                                                <strong>{f.title}</strong>
                                                <span>{f.desc}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </ScrollReveal>
                    </div>

                    {/* Mechanism close-up shots */}
                    <ScrollReveal delay={0.1}>
                        <div className={styles.detailStrip}>
                            {[
                                { src: '/brandbook/3_negro_acost.svg', label: 'Marco Negro + Amarillo', alt: 'Carátula amarilla sobre marco negro — vista frontal' },
                                { src: '/brandbook/5_zoom_negro.svg', label: 'Mecanismo magnético', alt: 'Detalle del clip magnético en el marco negro' },
                                { src: '/brandbook/4_trans_acost.svg', label: 'Marco Transparente + Amarillo', alt: 'Carátula amarilla sobre marco transparente' },
                                { src: '/brandbook/6_zoom_trans.svg', label: 'Clic — vista detalle', alt: 'Detalle del clip magnético en el marco transparente' },
                            ].map((d) => (
                                <div className={styles.detailCard} key={d.src}>
                                    <img src={d.src} alt={d.alt} loading="lazy" />
                                    <span className={styles.detailLabel}>{d.label}</span>
                                </div>
                            ))}
                        </div>
                    </ScrollReveal>
                </div>
            </section>

            {/* ── El Viaje del Plástico ── */}
            <PlasticJourney />

            {/* ── La Colección — Cover catalog ── */}
            <section className={styles.collectionSection}>
                <div className={styles.collectionInner}>
                    <ScrollReveal>
                        <div className={styles.collectionHeader}>
                            <div>
                                <p className={styles.eyebrow}>La colección</p>
                                <h2 className={styles.sectionTitle}>Cuatro colores.<br />Un solo marco.</h2>
                            </div>
                            <Button onClick={() => window.location.hash = 'products'} variant="outline">
                                Ver todos los productos
                            </Button>
                        </div>
                    </ScrollReveal>

                    <StaggerContainer className={styles.collectionGrid} staggerDelay={0.1}>
                        {covers.map((c) => (
                            <div key={c.id} className={styles.coverCard}>
                                <img src={c.image} alt={`Carátula magnética ${c.label}`} loading="lazy" />
                                <p className={styles.coverCardLabel}>
                                    <span className={styles.coverDot} style={{ background: c.color }} />
                                    {c.label}
                                </p>
                            </div>
                        ))}
                    </StaggerContainer>
                </div>
            </section>

            {/* ── El Kit — Unboxing ── */}
            <section className={styles.kitSection}>
                <div className={styles.kitInner}>
                    <ScrollReveal direction="left" className={styles.kitImageWrap}>
                        <img
                            src="/brandbook/16_bundle_negro.svg"
                            alt="Kit Tortu-k: caja, marco negro, las 4 carátulas, bolsa de red y paño de limpieza"
                            className={styles.kitImage}
                            loading="lazy"
                        />
                    </ScrollReveal>

                    <ScrollReveal direction="right" className={styles.kitTextWrap}>
                        <p className={styles.kitLabel}>El empaque</p>
                        <h2 className={styles.kitTitle}>
                            Un set de herramientas,<br />úsalas como quieras.
                        </h2>
                        <p className={styles.kitDescription}>
                            Un kit de colores para cualquier situación. Compartimentos donde
                            las carátulas encajan perfectamente, puedes coleccionarlas y llevarlas contigo..
                            La bolsa de red es el guiño al origen: Es hora de pescar.
                        </p>
                        <div className={styles.kitFeatures}>
                            {[
                                'Marco base (Negro o Transparente)',
                                '4 carátulas magnéticas intercambiables',
                                'Bolsa de red reciclada',
                                'Paño de limpieza Tortu-k',
                                'Tarjeta de historia y mensaje',
                            ].map((f) => (
                                <span className={styles.kitFeatureItem} key={f}>{f}</span>
                            ))}
                        </div>
                        <Button onClick={() => window.location.hash = 'products'} size="lg">
                            Consigue el tuyo
                        </Button>
                    </ScrollReveal>
                </div>
            </section>

            {/* ── Galería lifestyle — carousel ── */}
            <section className={styles.gallerySection}>
                <ScrollReveal>
                    <div className={styles.galleryHeader}>
                        <div>
                            <p className={styles.eyebrow}>Vive Tortu-k</p>
                            <h2 className={styles.sectionTitle}>
                                <span>En la playa</span><br />
                                <span>En el bosque</span><br />
                                <span>En tu rostro</span>
                            </h2>
                        </div>
                        <div className={styles.carouselNav}>
                            <button
                                className={styles.carouselArrow}
                                onClick={() => scrollCarousel('prev')}
                                aria-label="Imagen anterior"
                            >
                                ←
                            </button>
                            <button
                                className={styles.carouselArrow}
                                onClick={() => scrollCarousel('next')}
                                aria-label="Imagen siguiente"
                            >
                                →
                            </button>
                        </div>
                    </div>
                </ScrollReveal>

                <div 
                    className={styles.carouselTrack} 
                    ref={carouselRef}
                    onMouseDown={handleMouseDown}
                    onMouseLeave={handleMouseLeave}
                    onMouseUp={handleMouseUp}
                    onMouseMove={handleMouseMove}
                >
                    {galleryImages.map((img, i) => (
                        <div
                            key={i}
                            data-slide
                            className={styles.carouselSlide}
                            onClick={(e) => handleSlideClick(i, e)}
                            role="button"
                            tabIndex={0}
                            aria-label={`Abrir imagen: ${img.alt}`}
                            onKeyDown={(e) => e.key === 'Enter' && openLightbox(i)}
                        >
                            <img src={img.src} alt={img.alt} loading="lazy" />
                        </div>
                    ))}
                </div>
            </section>

            {/* ── CTA final ── */}
            <ScrollReveal>
                <section className={styles.ctaSection}>
                    <div className={styles.ctaInner}>
                        <p className={styles.eyebrow} style={{ justifyContent: 'center' }}>¿Listo?</p>
                        <h2 className={styles.ctaTitle}>
                            El mundo es tu materia prima.<br />Arma el tuyo.
                        </h2>
                        <div className={styles.ctaActions}>
                            <Button onClick={() => window.location.hash = 'customize'} size="lg">
                                Configurar mis lentes
                            </Button>
                            <Button onClick={() => window.location.hash = 'products'} variant="outline" size="lg">
                                Ver colección
                            </Button>
                        </div>
                    </div>
                </section>
            </ScrollReveal>

            <Lightbox
                images={galleryImages}
                currentIndex={lightboxIndex}
                isOpen={lightboxOpen}
                onClose={() => setLightboxOpen(false)}
                onNavigate={setLightboxIndex}
            />
        </div>
    )
}
